import { useCallback, useMemo } from 'react'
import { useTerminology } from '../providers/TerminologyProvider'
import { useSetupConfig } from './useSetupConfig'
import type { HierarchyLevel, TermKey } from '../types'

const LEVEL_TERMS: Record<HierarchyLevel, TermKey> = {
  ORGANIZATION: 'ORGANIZATION',
  DIVISION: 'DIVISION',
  VALUE_STREAM: 'VALUE_STREAM',
  PRODUCT_TEAM: 'PRODUCT_TEAM',
}

/**
 * Org-specific hierarchy level labels, plus which levels are active.
 *
 * Names now come from the tenant's terminology (English required, Norwegian
 * optional and falling back to English), not from the single un-localized
 * `hierarchyLabel*` string this used to read — that string was written with no
 * language recorded, so a Norwegian admin's "Divisjon" was shown to English
 * colleagues in the same tenant. V50 carried those labels into terminology as
 * English terms, so nobody loses a rename they already made.
 *
 * The signature is unchanged so the fifteen-odd call sites already using
 * `labelForLevel` need no edit.
 */
export function useHierarchyLabels() {
  const { term, isLoading: termsLoading } = useTerminology()
  const { config, isLoading: configLoading } = useSetupConfig()

  const labels = useMemo(() => ({
    organization: term('ORGANIZATION'),
    division: term('DIVISION'),
    valueStream: term('VALUE_STREAM'),
    productTeam: term('PRODUCT_TEAM'),
  }), [term])

  const activeHierarchyLevels = useMemo<HierarchyLevel[]>(
    () => config?.activeHierarchyLevels ?? ['ORGANIZATION', 'DIVISION', 'VALUE_STREAM', 'PRODUCT_TEAM'],
    [config?.activeHierarchyLevels]
  )

  const isLevelActive = useCallback(
    (level: HierarchyLevel): boolean => activeHierarchyLevels.includes(level),
    [activeHierarchyLevels]
  )

  const labelForLevel = useCallback(
    (level: HierarchyLevel): string => term(LEVEL_TERMS[level]),
    [term]
  )

  /** Plural form, for headings and counts ("3 Value Streams at risk"). */
  const pluralForLevel = useCallback(
    (level: HierarchyLevel): string => term(LEVEL_TERMS[level], true),
    [term]
  )

  return useMemo(() => ({
    ...labels,
    activeHierarchyLevels,
    isLevelActive,
    labelForLevel,
    pluralForLevel,
    isLoading: configLoading || termsLoading,
  }), [activeHierarchyLevels, configLoading, isLevelActive, labelForLevel, labels, pluralForLevel, termsLoading])
}
